import React from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom'; // Replacing withRouter with useNavigate
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { isExternal } from 'helpers/http';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';

import RichEditorContent from 'components/RichEditorContent';

const CmsElementCallToAction = (props) => {
  const {
    editMode,
    index,
    showElementSettings,
  } = props;

  const navigate = useNavigate();

  // CMS Logic via Hook (replaces AbstractCmsElement class logic)
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: index,
    disabled: !editMode
  });

  // Handle CTA Button click
  const handleButtonClick = (e) => {
    // Prevent event bubbling if in editMode so settings don't trigger twice
    if (editMode) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const buttonUrl = getParamValue('link', '');
    if (isExternal(buttonUrl)) {
      window.location.href = buttonUrl;
    } else {
      navigate(buttonUrl);
    }
  };

  // Drag and drop styles
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    width: '100%',
    cursor: editMode ? 'move' : 'default'
  };

  // UI Constants
  const buttonClass = shown ? 'button-shown' : 'button-not-shown-yet';
  const textAlign = getParamValue('textAlign', 'center');
  const mainColor = getParamValue('mainColor', 'master');

  const innerContent = (
    <div
      id={getMainId()}
      className={`
        ms-call-to-action-content p-b-40 p-t-40 p-l-60 p-r-60
        text-${mainColor}
        ${cssClasses}
        ${responsiveClasses}
      `}
      style={getBackgroundStyle()}
      onClick={(e) => editMode && showElementSettings(e, index)}
    >
      {/* Dynamic styles tag for custom animations/CSS */}
      {elementStyleTag}

      <div className={`call-to-action-container text-${textAlign}`}>
        <h2 className="no-margin p-b-25">{getParamValue('title', '')}</h2>

        <RichEditorContent content={'<p>' + getParamValue('content', '') + '</p>'} />

        <Button
          className={`
            call-to-action btn-animated from-left fa
            ${getParamValue('icon', 'fa-arrow-right')}
            ${buttonClass}
          `}
          variant="primary"
          onClick={handleButtonClick}
        >
          <span>{getParamValue('buttonTitle', '')}</span>
        </Button>
      </div>

      {getAppendHtml()}
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      {...attributes}
      {...listeners}
    >
      {/*
          Stable wrapper for ScrollTrigger (React 19 Fix).
          Ensures the trigger has a valid DOM reference during mounting.
      */}
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
};

export default CmsElementCallToAction;
